import { BreakpointObserver } from '@angular/cdk/layout';
import { NgOptimizedImage } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { Router, RouterModule } from '@angular/router';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { Subject, takeUntil } from 'rxjs';
import { Button } from '../../../components/common/button/button';
import { ConfirmDialog } from '../../../components/common/confirm-dialog/confirm-dialog';
import { Price } from '../../../components/common/price/price';
import { ShoppingCartEmpty } from '../../../components/shopping/shopping-cart-empty/shopping-cart-empty';
import { ShoppingCartItem } from '../../../models/shopping';
import { FileurlPipe } from '../../../pipes/fileurl.pipe';
import { ShoppingCartItemImagePipe } from '../../../pipes/shopping/shopping-cart-item-image-pipe';
import { ShoppingCartItemPricePipe } from '../../../pipes/shopping/shopping-cart-item-price-pipe';
import { ShoppingApiService } from '../../../services/apis/shopping-api.service';
import { ApiException, ApiStatus } from '../../../services/apis/status';
import { CartService } from '../../../services/cart.service';
import { TitleService } from '../../../services/title.service';
import {
  extractShoppingCartItemAttrs,
  extractShoppingCartItemPricedAttr,
  ProductAttrWithValue,
} from '../../../utils/cart';

@Component({
  selector: 'app-shopping-cart-page',
  imports: [
    RouterModule,
    MatTableModule,
    Price,
    MatIconModule,
    NzInputNumberModule,
    FormsModule,
    ShoppingCartItemPricePipe,
    NgOptimizedImage,
    ShoppingCartItemImagePipe,
    FileurlPipe,
    Button,
    ShoppingCartEmpty,
  ],
  templateUrl: './shopping-cart-page.html',
  styleUrl: './shopping-cart-page.scss',
})
export class ShoppingCartPage implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  constructor(
    private titleService: TitleService,
    private cartService: CartService,
    private breakpointObserver: BreakpointObserver,
    private dialog: MatDialog,
    private shoppingApi: ShoppingApiService,
    private router: Router
  ) {
    this.titleService.setTitle('Shopping Cart');
    this.breakpointObserver
      .observe(['(max-width: 660px)'])
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result.matches) {
          this.displayedColumns = ['product', 'total'];
        } else {
          this.displayedColumns = ['product', 'price', 'quantity', 'total'];
        }
      });
  }

  ngOnInit(): void {
    this.cartService.findShoppingCartItems();
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  displayedColumns: string[] = ['product', 'price', 'quantity', 'total'];

  get items(): ShoppingCartItem[] {
    return this.cartService.items();
  }

  getItemAttrs(item: ShoppingCartItem): ProductAttrWithValue[] {
    return extractShoppingCartItemAttrs(item);
  }

  getItemPricedAttr(item: ShoppingCartItem): ProductAttrWithValue | undefined {
    return extractShoppingCartItemPricedAttr(item);
  }

  get quantity(): number {
    return this.cartService.quantity();
  }

  get subtotal(): number {
    return this.cartService.subtotal();
  }

  onQuantityChanged(item: ShoppingCartItem) {
    if (!item.quantity) {
      return;
    }
    this.setShoppingCartItem(item, item.quantity);
  }
  loading: boolean = false;

  async setShoppingCartItem(item: ShoppingCartItem, quantity: number) {
    try {
      this.loading = true;
      await this.cartService.setShoppingCartItem({
        product_id: item.product.id,
        quantity: quantity,
        product_attr_value_ids: item.product_attr_value_ids,
        product_priced_attr_value_id: item.product_priced_attr_value_id,
      });
    } catch (error) {
    } finally {
      this.loading = false;
    }
  }

  remove(item: ShoppingCartItem) {
    this.dialog
      .open(ConfirmDialog, {
        width: '420px',
        data: {
          title: 'Remove Cart Item',
          content: `Are you sure to remove <b>${item.product.name}</b> from cart?`,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.setShoppingCartItem(item, 0);
        }
      });
  }

  async checkout() {
    try {
      this.loading = true;
      const r = await this.shoppingApi.createPaymentIntent();
      this.router.navigate(['checkout', r.id]);
    } catch (error) {
      if (error instanceof ApiException) {
        if (error.status === ApiStatus.SHOPPING_CART_EMPTY) {
          this.cartService.findShoppingCartItems();
        }
      }
    } finally {
      this.loading = false;
    }
  }
}
